import React from 'react';
import moment from 'moment';
import {
  FilterDropdownProps,
  ColumnsType,
  ColumnType,
} from 'antd/es/table/interface';
import cx from 'classnames';

import TableFilter from 'components/filters/TableFilter';
import BaseHighlighter from 'components/wrappers/BaseHighlighter';
import {
  ColumnValue,
  getFilterList,
} from 'components/filters/TableFilter/TableFilter';
import {
  FinancialReportFilters,
  FinancialReportFilterFieldEnum,
  FinancialReportMultipleFilterValues,
} from './utils';
import {
  FinancialReportSortFieldEnum,
  FinancialReportDetailsResponseModel,
  FinancialFilterViewModel,
} from 'api/backofficeApiClient';

interface FinancialReportColumns {
  filtersData?: FinancialReportMultipleFilterValues;
  filters: FinancialReportFilters;
  onChange: (
    type: FinancialReportFilterFieldEnum,
    newFilters: string[],
  ) => void;
  isFiltersDataLoaded: boolean;
  searchQuery: string;
  columnsSettings?: FinancialFilterViewModel;
}

const getHighlightedText = (text: string = '', search: string = '') => (
  <BaseHighlighter searchWords={[search]} textToHighlight={text} />
);

const isCheckActiveFilter = (data: ColumnValue[], filterValues: string[]) => {
  const filterList = getFilterList(data, filterValues);
  return Boolean(filterList.find((columnValue) => columnValue.checked));
};

interface ColumnParams extends Omit<FinancialReportColumns, 'searchQuery'> {
  title: string;
  searchPlaceholderTitle?: string;
  type: FinancialReportFilterFieldEnum;
}

interface CustomColumnsType
  extends ColumnType<FinancialReportDetailsResponseModel> {
  isVisible?: boolean;
}

const getColumnProps = (columnParams: ColumnParams) => {
  const {
    filtersData,
    filters,
    onChange,
    isFiltersDataLoaded,
    title,
    type,
    searchPlaceholderTitle,
  } = columnParams;

  const isActiveFilter = isCheckActiveFilter(
    filtersData ? filtersData[type] : [],
    filters.columns[type],
  );

  return {
    filterIcon: () => (
      <span
        className={cx('icon icon-filter', {
          active: isActiveFilter,
        })}
      />
    ),
    filterDropdown: (props: FilterDropdownProps) => (
      <TableFilter
        title={title}
        searchPlaceholderTitle={searchPlaceholderTitle}
        type={type}
        data={filtersData && filtersData[type]}
        onFilterChange={onChange}
        filterValues={filters.columns[type]}
        isLoaded={isFiltersDataLoaded}
        filterDropdownProps={props}
      />
    ),
  };
};

const isFixColumn = (count: number) => {
  return count > 3 ? 'left' : false;
};

export const getFinancialReportColumns = ({
  searchQuery,
  columnsSettings,
  ...anotherParams
}: FinancialReportColumns): ColumnsType<FinancialReportDetailsResponseModel> => {
  const countOfVisibleColumns = Object.values(columnsSettings || {}).filter(
    (setting) => setting,
  ).length;

  const columns: CustomColumnsType[] = [
    {
      title: 'Customer Name',
      key: FinancialReportSortFieldEnum.CustomerName,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="customer-name">
          {getHighlightedText(
            record.customerInfo?.customerName || '',
            searchQuery,
          )}
        </span>
      ),
      width: 150,
      fixed: isFixColumn(countOfVisibleColumns),
      ...getColumnProps({
        ...anotherParams,
        title: 'Customer Name',
        type: FinancialReportSortFieldEnum.CustomerName,
      }),
      isVisible: true,
    },
    {
      title: 'Member 1 Name',
      key: FinancialReportSortFieldEnum.Member1Name,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="member1-name">
          {getHighlightedText(record.member1?.name || '', searchQuery)}
        </span>
      ),
      width: 150,
      fixed: isFixColumn(countOfVisibleColumns),
      ...getColumnProps({
        ...anotherParams,
        title: 'Member 1 Name',
        type: FinancialReportSortFieldEnum.Member1Name,
      }),
      isVisible: columnsSettings?.isMember1Name,
    },
    {
      title: 'Member 2 Name',
      key: FinancialReportSortFieldEnum.Member2Name,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="member2-name">
          {getHighlightedText(record.member2?.name || '', searchQuery)}
        </span>
      ),
      width: 150,
      fixed: isFixColumn(countOfVisibleColumns),
      ...getColumnProps({
        ...anotherParams,
        title: 'Member 2 Name',
        type: FinancialReportSortFieldEnum.Member2Name,
      }),
      isVisible: columnsSettings?.isMember2Name,
    },
    {
      title: 'Worker Type / Worker ID',
      key: FinancialReportSortFieldEnum.WorkerTypeWorkerID,
      render: (record: FinancialReportDetailsResponseModel) => (
        <>
          {record.workerInfo?.workerType && (
            <div data-id="worker-type">
              {getHighlightedText(
                record.workerInfo?.workerType || '',
                searchQuery,
              )}
            </div>
          )}
          {record.workerInfo?.workerKey && (
            <div data-id="worker-key">
              {getHighlightedText(
                record.workerInfo?.workerKey || '',
                searchQuery,
              )}
            </div>
          )}
        </>
      ),
      width: 150,
      fixed: isFixColumn(countOfVisibleColumns),
      ...getColumnProps({
        ...anotherParams,
        title: 'Worker Type / Worker ID',
        searchPlaceholderTitle: 'Worker Type',
        type: FinancialReportSortFieldEnum.WorkerTypeWorkerID,
      }),
      isVisible: columnsSettings?.isWorkerTypeOrWorkerId,
    },
    {
      title: 'Parent Vendor',
      key: FinancialReportSortFieldEnum.ParentVendor,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="parent-vendor">
          {getHighlightedText(record.parentVendor || '', searchQuery)}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Parent Vendor',
        type: FinancialReportSortFieldEnum.ParentVendor,
      }),
      isVisible: columnsSettings?.isParentVendor,
    },
    {
      title: 'Worker Name',
      key: FinancialReportSortFieldEnum.WorkerName,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="worker-name">
          {getHighlightedText(record.workerInfo?.fullName || '', searchQuery)}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Worker Name',
        type: FinancialReportSortFieldEnum.WorkerName,
      }),
      isVisible: columnsSettings?.isWorkerName,
    },
    {
      title: 'Worker Job Title',
      key: FinancialReportSortFieldEnum.WorkerJobTitle,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="worker-job-title">
          {getHighlightedText(record.workerInfo?.jobTitle || '', searchQuery)}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Worker Job Title',
        type: FinancialReportSortFieldEnum.WorkerJobTitle,
      }),
      isVisible: columnsSettings?.isWorkerJobTitle,
    },
    {
      title: 'Commission Details',
      key: FinancialReportSortFieldEnum.CommissionDetails,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="commission-details">
          {getHighlightedText(record.commissionDetails || '', searchQuery)}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Commission Details',
        type: FinancialReportSortFieldEnum.CommissionDetails,
      }),
      isVisible: columnsSettings?.isCommissionDetails,
    },
    {
      title: 'Date',
      key: FinancialReportSortFieldEnum.Date,
      render: (record: FinancialReportDetailsResponseModel) => {
        const dateValue = record.date
          ? moment.utc(record.date).format('MM/DD/YYYY')
          : '';

        return (
          <span data-id="date">
            {getHighlightedText(dateValue || '', searchQuery)}
          </span>
        );
      },
      className: 'nowrap',
      isVisible: columnsSettings?.isDate,
    },
    {
      title: 'Member Markup',
      key: FinancialReportSortFieldEnum.MemberMarkup,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="member-markup">
          {getHighlightedText(
            record.mbpCalculations?.memberMarkupPercentage
              ? `${record.mbpCalculations.memberMarkupPercentage}`
              : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Member Markup',
        type: FinancialReportSortFieldEnum.MemberMarkup,
      }),
      isVisible: columnsSettings?.isMemberMarkup,
    },
    {
      title: 'Bill Hours (Reg)',
      key: FinancialReportSortFieldEnum.BillHoursReg,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="bill-hours-reg">
          {getHighlightedText(
            record.mbpCalculations?.billHoursRegular
              ? `${record.mbpCalculations.billHoursRegular}`
              : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Bill Hours (Reg)',
        type: FinancialReportSortFieldEnum.BillHoursReg,
      }),
      isVisible: columnsSettings?.isBillHoursReg,
    },
    {
      title: 'Bill Hours (OT)',
      key: FinancialReportSortFieldEnum.BillHoursOT,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="bill-hours-ot">
          {getHighlightedText(
            record.mbpCalculations?.billHoursOvertime
              ? `${record.mbpCalculations.billHoursOvertime}`
              : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Bill Hours (OT)',
        type: FinancialReportSortFieldEnum.BillHoursOT,
      }),
      isVisible: columnsSettings?.isBillHoursOt,
    },
    {
      title: 'Bill Rate (Reg)',
      key: FinancialReportSortFieldEnum.BillRateReg,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="bill-rate-reg">
          {getHighlightedText(
            record.mbpCalculations?.billRateRegular
              ? `${record.mbpCalculations.billRateRegular}`
              : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Bill Rate (Reg)',
        type: FinancialReportSortFieldEnum.BillRateReg,
      }),
      isVisible: columnsSettings?.isBillRateReg,
    },
    {
      title: 'Bill Rate (OT)',
      key: FinancialReportSortFieldEnum.BillRateOT,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="bill-rate-ot">
          {getHighlightedText(
            record.mbpCalculations?.billRateOvertime
              ? `${record.mbpCalculations.billRateOvertime}`
              : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Bill Rate (OT)',
        type: FinancialReportSortFieldEnum.BillRateOT,
      }),
      isVisible: columnsSettings?.isBillRateOt,
    },
    {
      title: 'Bill Amount',
      key: FinancialReportSortFieldEnum.BillAmount,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="bill-amount">
          {getHighlightedText(
            record.mbpCalculations?.billAmount
              ? `${record.mbpCalculations.billAmount}`
              : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Bill Amount',
        type: FinancialReportSortFieldEnum.BillAmount,
      }),
      isVisible: columnsSettings?.isBillAmount,
    },
    {
      title: 'Pay Hours (Reg)',
      key: FinancialReportSortFieldEnum.PayHoursReg,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="pay-hours-reg">
          {getHighlightedText(
            record.mbpCalculations?.payHoursRegular
              ? `${record.mbpCalculations.payHoursRegular}`
              : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Pay Hours (Reg)',
        type: FinancialReportSortFieldEnum.PayHoursReg,
      }),
      isVisible: columnsSettings?.isPayHoursReg,
    },
    {
      title: 'Pay Hours (OT)',
      key: FinancialReportSortFieldEnum.PayHoursOT,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="pay-hours-ot">
          {getHighlightedText(
            record.mbpCalculations?.payHoursOvertime
              ? `${record.mbpCalculations.payHoursOvertime}`
              : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Pay Hours (OT)',
        type: FinancialReportSortFieldEnum.PayHoursOT,
      }),
      isVisible: columnsSettings?.isPayHoursOt,
    },
    {
      title: 'Pay Rate (Reg)',
      key: FinancialReportSortFieldEnum.PayRateReg,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="pay-rate-reg">
          {getHighlightedText(
            record.mbpCalculations?.payRateRegular
              ? `${record.mbpCalculations.payRateRegular}`
              : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Pay Rate (Reg)',
        type: FinancialReportSortFieldEnum.PayRateReg,
      }),
      isVisible: columnsSettings?.isPayRateReg,
    },
    {
      title: 'Pay Rate (OT)',
      key: FinancialReportSortFieldEnum.PayRateOT,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="pay-rate-ot">
          {getHighlightedText(
            record.mbpCalculations?.payRateOvertime
              ? `${record.mbpCalculations.payRateOvertime}`
              : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Pay Rate (OT)',
        type: FinancialReportSortFieldEnum.PayRateOT,
      }),
      isVisible: columnsSettings?.isPayRateOt,
    },
    {
      title: 'Pay Amount',
      key: FinancialReportSortFieldEnum.PayAmount,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="pay-amount">
          {getHighlightedText(
            record.mbpCalculations?.payAmount
              ? `${record.mbpCalculations.payAmount}`
              : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Pay Amount',
        type: FinancialReportSortFieldEnum.PayAmount,
      }),
      isVisible: columnsSettings?.isPayAmount,
    },
    {
      title: 'MBP Rate',
      key: FinancialReportSortFieldEnum.MBPRate,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="mbp-rate">
          {getHighlightedText(
            record.mbpCalculations?.mbpInvoiceRatePercentage
              ? `${record.mbpCalculations.mbpInvoiceRatePercentage}`
              : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'MBP Rate',
        type: FinancialReportSortFieldEnum.MBPRate,
      }),
      isVisible: columnsSettings?.isMBPRate,
    },
    {
      title: 'MBP Fee ($)',
      key: FinancialReportSortFieldEnum.MBPFee,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="mbp-fee">
          {getHighlightedText(
            record.mbpCalculations?.mbpFee
              ? `${record.mbpCalculations.mbpFee}`
              : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'MBP Fee ($)',
        type: FinancialReportSortFieldEnum.MBPFee,
      }),
      isVisible: columnsSettings?.isMBPFee,
    },
    {
      title: 'MSP Fee',
      key: FinancialReportSortFieldEnum.MSPFee,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="msp-fee">
          {getHighlightedText(
            record.mbpCalculations?.mspFee
              ? `${record.mbpCalculations.mspFee}`
              : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'MSP Fee',
        type: FinancialReportSortFieldEnum.MSPFee,
      }),
      isVisible: columnsSettings?.isMSPFee,
    },
    {
      title: 'VMS Fee',
      key: FinancialReportSortFieldEnum.VMSFee,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="vms-fee">
          {getHighlightedText(
            record.mbpCalculations?.vmsFee
              ? `${record.mbpCalculations.vmsFee}`
              : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'VMS Fee',
        type: FinancialReportSortFieldEnum.VMSFee,
      }),
      isVisible: columnsSettings?.isVMSFee,
    },
    {
      title: 'Total Profit',
      key: FinancialReportSortFieldEnum.TotalProfit,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="total-profit">
          {getHighlightedText(
            record.mbpCalculations?.totalProfit
              ? `${record.mbpCalculations.totalProfit}`
              : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Total Profit',
        type: FinancialReportSortFieldEnum.TotalProfit,
      }),
      isVisible: columnsSettings?.isTotalProfit,
    },
    {
      title: 'Member 1 USD/Hr',
      key: FinancialReportSortFieldEnum.Member1USDHr,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="member1-usd-hr">
          {getHighlightedText(
            record.member1?.usdPerHourRate
              ? `${record.member1.usdPerHourRate}`
              : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Member 1 USD/Hr',
        type: FinancialReportSortFieldEnum.Member1USDHr,
      }),
      isVisible: columnsSettings?.isMember1USDHr,
    },
    {
      title: 'Member 2 USD/Hr',
      key: FinancialReportSortFieldEnum.Member2USDHr,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="member2-usd-hr">
          {getHighlightedText(
            record.member2?.usdPerHourRate
              ? `${record.member2.usdPerHourRate}`
              : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Member 2 USD/Hr',
        type: FinancialReportSortFieldEnum.Member2USDHr,
      }),
      isVisible: columnsSettings?.isMember2USDHr,
    },
    {
      title: 'Member 1 Profit %',
      key: FinancialReportSortFieldEnum.Member1ProfitPercent,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="member1-profit-percentage">
          {getHighlightedText(
            record.member1?.profitPercentage
              ? `${record.member1.profitPercentage}`
              : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Member 1 Profit %',
        type: FinancialReportSortFieldEnum.Member1ProfitPercent,
      }),
      isVisible: columnsSettings?.isMember1ProfitPercent,
    },
    {
      title: 'Member 2 Profit %',
      key: FinancialReportSortFieldEnum.Member2ProfitPercent,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="member2-profit-percentage">
          {getHighlightedText(
            record.member2?.profitPercentage
              ? `${record.member2.profitPercentage}`
              : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Member 2 Profit %',
        type: FinancialReportSortFieldEnum.Member2ProfitPercent,
      }),
      isVisible: columnsSettings?.isMember2ProfitPercent,
    },
    {
      title: 'Member 1 Profit',
      key: FinancialReportSortFieldEnum.Member1Profit,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="member1-profit">
          {getHighlightedText(
            record.member1?.profit ? `${record.member1.profit}` : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Member 1 Profit',
        type: FinancialReportSortFieldEnum.Member1Profit,
      }),
      isVisible: columnsSettings?.isMember1Profit,
    },
    {
      title: 'Member 2 Profit',
      key: FinancialReportSortFieldEnum.Member2Profit,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="member2-profit">
          {getHighlightedText(
            record.member2?.profit ? `${record.member2.profit}` : '',
            searchQuery,
          )}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Member 2 Profit',
        type: FinancialReportSortFieldEnum.Member2Profit,
      }),
      isVisible: columnsSettings?.isMember2Profit,
    },
    {
      title: 'Invoice Number',
      key: FinancialReportSortFieldEnum.InvoiceNumber,
      render: (record: FinancialReportDetailsResponseModel) => (
        <span data-id="invoice-number">
          {getHighlightedText(record.invoiceNumber || '', searchQuery)}
        </span>
      ),
      ...getColumnProps({
        ...anotherParams,
        title: 'Invoice Number',
        type: FinancialReportSortFieldEnum.InvoiceNumber,
      }),
      isVisible: columnsSettings?.isInvoiceNumber,
    },
  ];

  return columns
    .filter((column) => column?.isVisible)
    .map((column) => {
      const { isVisible, ...anotherParams } = column;
      return anotherParams;
    });
};
